import { findMap } from "$root/utils";
import type { MaybeAsync } from "@mdotm/utils/promise";
import { type FieldSize, Icon, type Option } from "@mdotm/shadcn/legacy/components";
import {
	Button,
	type ButtonPalette,
	type DataAttributesProps,
	Select,
	type SelectTriggerProps,
} from "@mdotm/shadcn/mdotm/components";
import {
	type ClassList,
	type NodeOrFn,
	renderNodeOrFn,
	toClassListRecord,
	toClassName,
	useDrivenState,
} from "@mdotm/shadcn/legacy/react-extensions";
import { forwardRef } from "react";

/** Extra props threaded to the trigger via the Select's `extraTriggerProps`. */
type ButtonSelectTriggerExtraProps = {
	palette?: ButtonPalette;
	fieldSize?: FieldSize;
};

/**
 * Trigger for `ButtonWithSelect` rendered as the right-hand caret button. Forwards the ref so the
 * Radix popover (rendered via `PopoverTrigger asChild`) can anchor to it, and spreads the
 * Radix-injected props (`onClick`, `aria-*`, `data-state`) onto the underlying `<Button>`.
 */
const ButtonSelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps & ButtonSelectTriggerExtraProps>(
	function ButtonSelectTrigger(
		{
			open,
			triggerLabel: _triggerLabel,
			isEmpty: _isEmpty,
			isMulti: _isMulti,
			invalid,
			disabled,
			size: _size,
			enableClear: _enableClear,
			onClear: _onClear,
			onKeyDown,
			palette,
			fieldSize,
			...rest
		},
		ref,
	) {
		return (
			<Button
				innerRef={ref}
				data-invalid={invalid}
				disabled={disabled}
				onKeyDown={onKeyDown}
				style={{
					borderTopLeftRadius: 0,
					borderBottomLeftRadius: 0,
					borderLeftWidth: 0,
				}}
				palette={palette}
				size={fieldSize}
				{...rest}
			>
				<span className="transition-transform data-[open=true]:transform-[rotateX(180deg)] flex" data-open={open}>
					<Icon icon="Down" />
				</span>
			</Button>
		);
	},
);

export type ButtonWithSelectProps<T> = {
	disabled?: boolean;
	children?: NodeOrFn;
	options?: Array<Option<T>>;
	value?: T;
	palette?: ButtonPalette;
	onChange?(v: T): void;
	size?: FieldSize;
	enableSearch?: boolean;
	dataAttributes?: {
		buttonDataAttributes?: DataAttributesProps;
		selectDataAttributes?: DataAttributesProps;
		selectTriggerDataAttributes?: DataAttributesProps;
	};
	className?: ClassList;
} & (
	| {
			onClick(v: T): void;
			onClickAsync?: undefined;
	  }
	| {
			onClick?: undefined;
			onClickAsync: MaybeAsync<void, [T]>;
	  }
);

export function ButtonWithSelect<T>(props: ButtonWithSelectProps<T>): React.JSX.Element {
	const [value, setValue] = useDrivenState<T | null>(props.value ?? null, { onSet: props.onChange });
	const noOptionAvailable = !props.options?.length;
	return (
		<div className={toClassName({ flex: true, ...toClassListRecord(props.className) })}>
			<Button
				disabled={props.disabled || value === null}
				{...(props.onClickAsync
					? { onClickAsync: () => props.onClickAsync(value!) }
					: { onClick: () => props.onClick(value!) })}
				style={
					noOptionAvailable
						? {}
						: {
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
							}
				}
				palette={props.palette}
				size={props.size}
				{...(props.dataAttributes?.buttonDataAttributes ?? {})}
			>
				{props.children
					? renderNodeOrFn(props.children)
					: value == null
						? "Select..."
						: (findMap(props.options ?? [], (opt) => (opt.value === value ? [opt.label] : [])) ?? "-")}
			</Button>
			{noOptionAvailable ? null : (
				<Select
					{...(props.dataAttributes?.selectDataAttributes ?? {})}
					triggerDataAttrs={props.dataAttributes?.selectTriggerDataAttributes}
					Trigger={ButtonSelectTrigger}
					extraTriggerProps={{ palette: props.palette, fieldSize: props.size }}
					value={value}
					options={props.options!}
					onChange={(v) => setValue(v)}
					enableSearch={props.enableSearch}
					floatingAppearance={{
						className: { "max-w-[16rem]! max-h-52!": props.size === "small" },
					}}
				/>
			)}
		</div>
	);
}
